import { FloatingPortal } from "@floating-ui/react"
import { useCallback, useEffect, useRef, useState } from "react"

export function PhotoGrid({ srcs, alts }: { srcs: string[]; alts: string[] }) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [visible, setVisible] = useState(false)
  const overlayRef = useRef<HTMLDivElement>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const open = (index: number) => {
    setSelectedIndex(index)
    requestAnimationFrame(() => setVisible(true))
  }

  const close = useCallback(() => {
    setVisible(false)
    closeTimerRef.current = setTimeout(() => setSelectedIndex(null), 200)
  }, [])

  useEffect(() => {
    if (selectedIndex === null) return
    overlayRef.current?.focus()
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = ""
    }
  }, [selectedIndex])

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (selectedIndex === null) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") close()
    }
    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [close, selectedIndex])

  return (
    <>
      <div className="my-6 grid grid-cols-3 gap-3">
        {srcs.map((src, i) => (
          <button
            key={i}
            type="button"
            onClick={() => open(i)}
            className="appearance-none border-0 bg-transparent p-0"
          >
            <img
              src={src}
              alt={alts[i]?.trim() || ""}
              loading="lazy"
              decoding="async"
              className="aspect-4/3 w-full cursor-pointer rounded-lg border border-(--border-default) object-cover transition-opacity hover:opacity-90"
            />
          </button>
        ))}
      </div>
      {selectedIndex !== null && (
        <FloatingPortal>
          <div
            ref={overlayRef}
            className={`fixed inset-0 z-modal flex cursor-pointer items-center justify-center transition-colors duration-200 ${visible ? "bg-black/70" : "bg-black/0"}`}
            role="dialog"
            aria-modal="true"
            aria-label="Expanded photo"
            tabIndex={-1}
          >
            <button
              type="button"
              aria-label="Close expanded photo"
              className="absolute inset-0 cursor-pointer appearance-none border-0 bg-transparent p-0"
              onClick={close}
            />
            <img
              src={srcs[selectedIndex]}
              alt={alts[selectedIndex]?.trim() || ""}
              decoding="async"
              className={`relative max-h-[60vh] max-w-[60vw] rounded-lg shadow-2xl transition-all duration-200 ${visible ? "scale-100 opacity-100" : "scale-75 opacity-0"}`}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  )
}
